import { gql } from '@apollo/client';
import type { ApolloClient } from '@apollo/client';

/**
 * Keystone's upload handshake: `createFile` reserves a row and signs a PUT,
 * the bytes go straight to the bucket, then `validateFile` promotes the row
 * once Keystone has checked the stored object against what was declared.
 *
 * `canonicalize` defaults to false: a lender document is served as uploaded,
 * so asking Bindery for a canonical PDF and a page rail would buy artifacts
 * nothing reads. The Files view can pass `true` when it adopts this.
 */

export const CREATE_FILE = gql`
  mutation CreateFile($input: FileCreateInput!) {
    createFile(input: $input) {
      file {
        id
      }
      signedUploadUrl
    }
  }
`;

export const VALIDATE_FILE = gql`
  mutation ValidateFile($fileId: UUID!, $canonicalize: Boolean!) {
    validateFile(fileId: $fileId, processing: { canonicalize: $canonicalize }) {
      id
      filename
      contentType
      sizeBytes
      status
    }
  }
`;

export interface CreateFileResult {
  createFile: { file: { id: string }; signedUploadUrl: string };
}

export interface ValidateFileResult {
  validateFile: {
    id: string;
    filename: string | null;
    contentType: string | null;
    sizeBytes: number | null;
    status: string | null;
  };
}

export type UploadStage = 'reserving' | 'uploading' | 'validating';

export interface UploadProgress {
  stage: UploadStage;
  /** 0..1 while the bytes are in flight; null when the stage has no measure. */
  fraction: number | null;
}

/** PUTs a body to a signed URL. Swappable so a test never opens a socket. */
export type PutObject = (
  url: string,
  file: Blob,
  contentType: string,
  onProgress: (fraction: number) => void,
) => Promise<void>;

export interface UploadOptions {
  canonicalize?: boolean;
  onProgress?: (progress: UploadProgress) => void;
  put?: PutObject;
}

export class UploadError extends Error {
  constructor(
    readonly stage: UploadStage,
    cause: unknown,
  ) {
    super(cause instanceof Error ? cause.message : String(cause), { cause });
    this.name = 'UploadError';
  }
}

export function contentTypeOf(file: File): string {
  return file.type || 'application/octet-stream';
}

/** XHR rather than fetch: fetch cannot report upload progress. */
export const putWithXhr: PutObject = (url, file, contentType, onProgress) =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url);
    request.setRequestHeader('Content-Type', contentType);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded / event.total);
      }
    };
    request.onload = () =>
      request.status >= 200 && request.status < 300
        ? resolve()
        : reject(new Error(`Storage answered ${request.status}`));
    request.onerror = () => reject(new Error('The upload could not reach storage'));
    request.send(file);
  });

/** Upload one file and return it validated. Throws `UploadError` naming the stage. */
export async function uploadFile(
  client: ApolloClient,
  file: File,
  { canonicalize = false, onProgress = () => {}, put = putWithXhr }: UploadOptions = {},
): Promise<ValidateFileResult['validateFile']> {
  const contentType = contentTypeOf(file);

  onProgress({ stage: 'reserving', fraction: null });
  let reservation: CreateFileResult['createFile'];
  try {
    const { data } = await client.mutate<CreateFileResult>({
      mutation: CREATE_FILE,
      variables: { input: { filename: file.name, contentType, sizeBytes: file.size } },
    });
    if (!data) {
      throw new Error('Keystone answered without a file');
    }
    reservation = data.createFile;
  } catch (cause) {
    throw new UploadError('reserving', cause);
  }

  onProgress({ stage: 'uploading', fraction: 0 });
  try {
    await put(reservation.signedUploadUrl, file, contentType, (fraction) =>
      onProgress({ stage: 'uploading', fraction }),
    );
  } catch (cause) {
    throw new UploadError('uploading', cause);
  }

  onProgress({ stage: 'validating', fraction: null });
  try {
    const { data } = await client.mutate<ValidateFileResult>({
      mutation: VALIDATE_FILE,
      variables: { fileId: reservation.file.id, canonicalize },
    });
    if (!data) {
      throw new Error('Keystone answered without a file');
    }
    return data.validateFile;
  } catch (cause) {
    throw new UploadError('validating', cause);
  }
}
